import { gsap } from "gsap";

import { createCursorController } from "./utils/cursor.js";
import { initializeSplitText } from "./utils/split-text.js";
import { createHorizontalScroll } from "./utils/scroll.js";

export function initGalleryExperience() {
  const scrollWrapper = document.querySelector('[data-scroll="wrapper"]');
  const scrollContent = document.querySelector('[data-scroll="content"]');
  const galleryImagesWrapper = [
    ...document.querySelectorAll('[data-gallery="image-wrapper"]'),
  ];
  const sectionContent = document.querySelector('[data-content="section"]');
  const detailPanels = [
    ...document.querySelectorAll('[data-content="details"]'),
  ];
  const detailWrappers = [
    ...document.querySelectorAll('[data-content="details-wrapper"]'),
  ];
  const cursor = document.querySelector('[data-cursor="container"]');

  if (!scrollWrapper || !scrollContent || !sectionContent || !cursor) {
    return;
  }

  const lenis = createHorizontalScroll({
    wrapper: scrollWrapper,
    content: scrollContent,
  });
  const cursorController = createCursorController(cursor);
  const splitsByIndex = [];

  let currentOpenIndex = null;
  let isAnimating = false;
  let currentAnimation = null;

  const updateScroll = (time) => {
    lenis.raf(time * 1000);
  };
  gsap.ticker.add(updateScroll);
  gsap.ticker.lagSmoothing(0);

  scrollWrapper.classList.remove("is-hidden");
  sectionContent.classList.add("is-hidden");

  detailPanels.forEach((panel, index) => {
    panel.classList.add("is-hidden");
    splitsByIndex[index] = initializeSplitText(panel);
  });

  detailWrappers.forEach((wrapper) => {
    const detailImage = wrapper.querySelector('[data-content="image"]');
    detailImage?.remove();
  });

  const openContent = (index) => {
    if (isAnimating || currentOpenIndex !== null || index < 0 || index >= detailPanels.length) {
      return;
    }

    const targetPanel = detailPanels[index];
    const targetWrapper = detailWrappers[index];
    const galleryItem = galleryImagesWrapper[index];
    const galleryImage = galleryItem?.querySelector('[data-gallery="image"]');
    const splitSet = splitsByIndex[index];

    if (!targetPanel || !targetWrapper || !galleryImage || !splitSet) {
      return;
    }

    const startRect = galleryImage.getBoundingClientRect();

    isAnimating = true;
    currentOpenIndex = index;
    galleryItem.setAttribute("aria-expanded", "true");
    lenis.stop();
    cursorController.open();
    cursorController.show();

    sectionContent.classList.remove("is-hidden");
    targetPanel.classList.remove("is-hidden");

    const targetRect = targetWrapper.getBoundingClientRect();
    const deltaX = startRect.left - targetRect.left;
    const deltaY = startRect.top - targetRect.top;

    gsap.set(galleryImage, {
      x: deltaX,
      y: deltaY,
      width: startRect.width,
      height: startRect.height,
    });

    targetWrapper.appendChild(galleryImage);

    const otherImages = galleryImagesWrapper.filter((_, imageIndex) => imageIndex !== index);

    currentAnimation = gsap.timeline({
      onComplete: () => {
        scrollWrapper.classList.add("is-hidden");
        gsap.set(galleryImage, { width: "100%", height: "100%" });
        isAnimating = false;
        currentAnimation = null;
      },
    });

    currentAnimation
      .fromTo(
        galleryImage,
        {
          x: deltaX,
          y: deltaY,
          width: startRect.width,
          height: startRect.height,
        },
        {
          x: 0,
          y: 0,
          width: targetRect.width,
          height: targetRect.height,
          duration: 1.15,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        otherImages,
        {
          clipPath: "inset(100% 0 0 0)",
          opacity: 0,
          duration: 0.65,
          ease: "power2.inOut",
        },
        0,
      )
      .fromTo(
        splitSet.titleTopChars,
        {
          yPercent: -100,
          opacity: 0,
        },
        {
          yPercent: 0,
          opacity: 1,
          duration: 0.75,
          stagger: 0.02,
          ease: "power3.out",
        },
        0.35,
      )
      .fromTo(
        splitSet.titleBottomChars,
        {
          yPercent: 100,
          opacity: 0,
        },
        {
          yPercent: 0,
          opacity: 1,
          duration: 0.75,
          stagger: 0.02,
          ease: "power3.out",
        },
        0.35,
      )
      .fromTo(
        splitSet.leftLines,
        {
          xPercent: -35,
          opacity: 0,
        },
        {
          xPercent: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.025,
          ease: "power3.out",
        },
        0.4,
      )
      .fromTo(
        splitSet.rightLines,
        {
          xPercent: 35,
          opacity: 0,
        },
        {
          xPercent: 0,
          opacity: 1,
          duration: 0.7,
          stagger: 0.025,
          ease: "power3.out",
        },
        0.4,
      );
  };

  const hideContent = (index) => {
    if (isAnimating || currentOpenIndex !== index) {
      return;
    }

    const targetWrapper = detailWrappers[index];
    const targetPanel = detailPanels[index];
    const galleryItem = galleryImagesWrapper[index];
    const galleryImage = targetWrapper?.querySelector('[data-gallery="image"]');
    const splitSet = splitsByIndex[index];

    if (!targetWrapper || !targetPanel || !galleryImage || !splitSet || !galleryItem) {
      return;
    }

    isAnimating = true;
    cursorController.close();

    scrollWrapper.classList.remove("is-hidden");
    scrollWrapper.style.zIndex = "20";

    const galleryRect = galleryItem.getBoundingClientRect();
    const targetRect = targetWrapper.getBoundingClientRect();
    const deltaX = galleryRect.left - targetRect.left;
    const deltaY = galleryRect.top - targetRect.top;

    const otherImages = galleryImagesWrapper.filter((_, imageIndex) => imageIndex !== index);

    currentAnimation = gsap.timeline({
      onComplete: () => {
        galleryItem.appendChild(galleryImage);
        targetPanel.classList.add("is-hidden");
        sectionContent.classList.add("is-hidden");
        scrollWrapper.style.zIndex = "";
        gsap.set(galleryImage, { clearProps: "all" });
        gsap.set(galleryImagesWrapper, { clearProps: "clipPath,opacity" });
        gsap.set(
          [
            ...splitSet.titleTopChars,
            ...splitSet.titleBottomChars,
            ...splitSet.leftLines,
            ...splitSet.rightLines,
          ],
          { clearProps: "all" },
        );
        galleryItem.setAttribute("aria-expanded", "false");
        lenis.start();
        isAnimating = false;
        currentOpenIndex = null;
        currentAnimation = null;
      },
    });

    currentAnimation
      .fromTo(
        galleryImage,
        {
          x: 0,
          y: 0,
          width: targetRect.width,
          height: targetRect.height,
        },
        {
          x: deltaX,
          y: deltaY,
          width: galleryRect.width,
          height: galleryRect.height,
          duration: 1.15,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        splitSet.titleTopChars,
        {
          yPercent: -100,
          opacity: 0,
          duration: 0.45,
          stagger: 0.01,
          ease: "power3.in",
        },
        0,
      )
      .to(
        splitSet.titleBottomChars,
        {
          yPercent: 100,
          opacity: 0,
          duration: 0.45,
          stagger: 0.01,
          ease: "power3.in",
        },
        0,
      )
      .to(
        splitSet.leftLines,
        {
          xPercent: -35,
          opacity: 0,
          duration: 0.4,
          stagger: 0.015,
          ease: "power3.in",
        },
        0,
      )
      .to(
        splitSet.rightLines,
        {
          xPercent: 35,
          opacity: 0,
          duration: 0.4,
          stagger: 0.015,
          ease: "power3.in",
        },
        0,
      )
      .to(
        otherImages,
        {
          clipPath: "inset(0% 0 0 0)",
          opacity: 1,
          duration: 0.7,
          ease: "power2.inOut",
        },
        0.15,
      );
  };

  galleryImagesWrapper.forEach((image, index) => {
    image.addEventListener("click", () => openContent(index));
    image.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openContent(index);
      }
    });
    image.addEventListener("touchstart", (event) => {
      if (event.touches.length === 1) {
        openContent(index);
      }
    });
    image.addEventListener("mouseenter", cursorController.show);
    image.addEventListener("mouseleave", cursorController.hide);
  });

  detailPanels.forEach((panel, index) => {
    panel.addEventListener("click", () => hideContent(index));
    panel.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        hideContent(index);
      }
    });
    panel.addEventListener("touchstart", (event) => {
      if (event.touches.length === 1) {
        hideContent(index);
      }
    });
    panel.addEventListener("mouseenter", cursorController.show);
    panel.addEventListener("mouseleave", cursorController.hide);
  });

  const handleGlobalKeydown = (event) => {
    if (event.key === "Escape" && currentOpenIndex !== null) {
      hideContent(currentOpenIndex);
    }
  };

  window.addEventListener("keydown", handleGlobalKeydown);
  window.addEventListener("mousemove", cursorController.update);

  const cleanup = () => {
    gsap.ticker.remove(updateScroll);
    lenis.destroy();
    cursorController.destroy();
    window.removeEventListener("keydown", handleGlobalKeydown);
    window.removeEventListener("mousemove", cursorController.update);
  };

  window.addEventListener("beforeunload", cleanup);

  return {
    lenis,
    openContent,
    hideContent,
    cleanup,
  };
}
